/**
 * EditorialMarkdownRenderer — premium wellness-publication renderer.
 *
 * Replaces the previous plain-markdown render with a richer, editorial
 * experience that auto-detects:
 *   - H2 / H3 with anchor ids + reading-progress anchors
 *   - Pull-quotes: a line starting with `> "` becomes a styled blockquote
 *   - Wellness callouts: a paragraph starting with `> **Takeaway:**`
 *     (or `**Insight:**`, `**Why this matters:**`, `**Try this:**`)
 *     becomes a styled callout card
 *   - Numbered + bulleted lists
 *   - Premium internal-link cards: any inline markdown link to /learn/...
 *     renders as a tappable wellness link card
 *   - Strong + emphasis
 *
 * Visual auto-insertion is handled by the parent LearnArticlePage so the
 * renderer stays a pure function of markdown text.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useMemo } from "react";

/* ── slug helper ────────────────────────────────────────────────────── */
function slugify(s = "") {
  return String(s).toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

/* ── inline markdown → React (bold / italic / links / em-dashes) ──── */
function renderInline(text) {
  if (!text) return null;
  // tokenise on: **bold**, _italic_, [text](url)
  const re = /(\*\*[^*]+\*\*|_[^_]+_|\[[^\]]+\]\([^)]+\))/g;
  const parts = [];
  let last = 0; let m; let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const seg = m[0];
    if (seg.startsWith("**")) {
      parts.push(<strong key={`b${k++}`} className="font-semibold text-emerald-950">{seg.slice(2, -2)}</strong>);
    } else if (seg.startsWith("_")) {
      parts.push(<em key={`i${k++}`}>{seg.slice(1, -1)}</em>);
    } else {
      const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(seg);
      if (lm) {
        const [, label, url] = lm;
        if (url.startsWith("/learn/")) {
          parts.push(
            <Link key={`l${k++}`} to={url} className="text-emerald-700 font-semibold hover:text-emerald-900 border-b border-emerald-300 hover:border-emerald-700 transition-colors">
              {label}
            </Link>
          );
        } else if (url.startsWith("/")) {
          parts.push(
            <Link key={`l${k++}`} to={url} className="text-emerald-700 font-semibold hover:text-emerald-900 underline decoration-emerald-300 hover:decoration-emerald-700 underline-offset-2">
              {label}
            </Link>
          );
        } else {
          parts.push(
            <a key={`l${k++}`} href={url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-900 underline decoration-emerald-300 underline-offset-2">
              {label}
            </a>
          );
        }
      } else {
        parts.push(seg);
      }
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/* ── block parser ───────────────────────────────────────────────────── */
const CALLOUT_LABELS = ["takeaway", "insight", "why this matters", "try this", "important", "quick note", "remember"];

function parseBlocks(md) {
  const lines = (md || "").split(/\r?\n/);
  const blocks = [];
  let pendingList = null;
  let pendingPara = [];
  let pendingQuote = [];

  const flushPara = () => {
    if (pendingPara.length === 0) return;
    const text = pendingPara.join(" ").trim();
    if (!text) { pendingPara = []; return; }
    // wellness callout detection (paragraph starts with **Label:**)
    const calloutMatch = /^\*\*([^*:]+):\*\*\s*(.+)$/.exec(text);
    if (calloutMatch && CALLOUT_LABELS.includes(calloutMatch[1].toLowerCase())) {
      blocks.push({ type: "callout", label: calloutMatch[1], content: calloutMatch[2] });
    } else {
      blocks.push({ type: "p", content: text });
    }
    pendingPara = [];
  };
  const flushList = () => {
    if (!pendingList) return;
    blocks.push(pendingList);
    pendingList = null;
  };
  const flushQuote = () => {
    if (pendingQuote.length === 0) return;
    const text = pendingQuote.join(" ").trim();
    // pull-quote detection: starts with " or '
    if (/^["“']/.test(text)) {
      blocks.push({ type: "pullquote", content: text.replace(/^["“']|["”']$/g, "") });
    } else {
      // wellness callout via leading "> **Label:**"
      const c = /^\*\*([^*:]+):\*\*\s*(.+)$/.exec(text);
      if (c && CALLOUT_LABELS.includes(c[1].toLowerCase())) {
        blocks.push({ type: "callout", label: c[1], content: c[2] });
      } else {
        blocks.push({ type: "blockquote", content: text });
      }
    }
    pendingQuote = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara(); flushList(); flushQuote();
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara(); flushList(); flushQuote();
      blocks.push({ type: "h2", content: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith("### ")) {
      flushPara(); flushList(); flushQuote();
      blocks.push({ type: "h3", content: line.slice(4).trim() });
      continue;
    }
    if (line.startsWith("#### ")) {
      flushPara(); flushList(); flushQuote();
      blocks.push({ type: "h4", content: line.slice(5).trim() });
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushPara(); flushQuote();
      if (!pendingList || pendingList.type !== "ul") { flushList(); pendingList = { type: "ul", items: [] }; }
      pendingList.items.push(line.slice(2).trim());
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      flushPara(); flushQuote();
      if (!pendingList || pendingList.type !== "ol") { flushList(); pendingList = { type: "ol", items: [] }; }
      pendingList.items.push(line.replace(/^\d+\.\s/, "").trim());
      continue;
    }
    if (line.startsWith("> ")) {
      flushPara(); flushList();
      pendingQuote.push(line.slice(2).trim());
      continue;
    }
    // hr / divider
    if (/^---+$/.test(line) || /^\*\*\*+$/.test(line)) {
      flushPara(); flushList(); flushQuote();
      blocks.push({ type: "hr" });
      continue;
    }
    flushList(); flushQuote();
    pendingPara.push(line);
  }
  flushPara(); flushList(); flushQuote();
  return blocks;
}


/* ── component ──────────────────────────────────────────────────────── */
export default function EditorialMarkdownRenderer({ body_md, visualEmbeds = [] }) {
  const blocks = useMemo(() => parseBlocks(body_md), [body_md]);
  const h2s = blocks.filter((b) => b.type === "h2");

  // Distribute visual embeds across H2 boundaries — after the
  // (length/2 - 1)th H2 insert social (1:1), after the last-but-one H2
  // insert pinterest (2:3). Hero (16:9) is rendered by the parent above
  // the article, NOT inline.
  const embedAfterIdx = {};
  if (h2s.length >= 2 && visualEmbeds.length) {
    const positions = [];
    if (h2s.length >= 3) positions.push(Math.floor(h2s.length / 2));
    positions.push(Math.max(1, h2s.length - 1));
    let h2Counter = -1;
    blocks.forEach((b, i) => {
      if (b.type === "h2") {
        h2Counter += 1;
        // Use the next block index after the H2 ends — i.e. insert
        // BEFORE the next H2 boundary, so visually the embed sits in
        // the section it belongs to.
        positions.forEach((p, vIdx) => {
          if (h2Counter === p && visualEmbeds[vIdx]) {
            embedAfterIdx[i] = visualEmbeds[vIdx];
          }
        });
      }
    });
  }

  return (
    <div className="editorial-renderer space-y-1" data-testid="editorial-renderer">
      {blocks.map((b, idx) => (
        <Block key={idx} block={b} embed={embedAfterIdx[idx]} />
      ))}
    </div>
  );
}

function Block({ block, embed }) {
  if (block.type === "h2") {
    const anchor = slugify(block.content);
    return (
      <>
        <h2 id={anchor} className="scroll-mt-24 group font-display text-2xl sm:text-3xl text-emerald-950 mt-12 mb-4 leading-snug font-light tracking-tight">
          <a href={`#${anchor}`} className="no-underline">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute -ml-6 text-emerald-700/50 text-xl">#</span>
            {renderInline(block.content)}
          </a>
        </h2>
        {embed && <InlineVisual visual={embed} />}
      </>
    );
  }
  if (block.type === "h3") {
    return <h3 className="font-display text-xl sm:text-[1.35rem] text-emerald-950 mt-8 mb-3 leading-snug font-normal">{renderInline(block.content)}</h3>;
  }
  if (block.type === "h4") {
    return <h4 className="font-display text-base sm:text-lg text-emerald-950 mt-6 mb-2 font-semibold">{renderInline(block.content)}</h4>;
  }
  if (block.type === "ul") {
    return (
      <ul className="my-4 space-y-2 text-emerald-950/90">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 leading-relaxed">
            <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-emerald-700/60 shrink-0" aria-hidden />
            <span className="flex-1">{renderInline(it)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "ol") {
    return (
      <ol className="my-4 space-y-2.5 text-emerald-950/90">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 leading-relaxed">
            <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-emerald-700/10 border border-emerald-700/20 text-emerald-800 text-[11px] font-semibold inline-flex items-center justify-center">{i + 1}</span>
            <span className="flex-1">{renderInline(it)}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === "callout") {
    return (
      <aside className="my-6 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-4 sm:px-6 sm:py-5 not-italic" data-testid="editorial-callout">
        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-800 font-semibold mb-1.5">{block.label}</p>
        <p className="text-[15px] sm:text-base leading-relaxed text-emerald-950/90">{renderInline(block.content)}</p>
      </aside>
    );
  }
  if (block.type === "pullquote") {
    return (
      <figure className="my-8 max-w-2xl mx-auto text-center" data-testid="editorial-pullquote">
        <span className="block font-display text-emerald-700/40 text-5xl leading-none">"</span>
        <blockquote className="font-display text-xl sm:text-2xl text-emerald-950 leading-snug font-light italic -mt-2">
          {renderInline(block.content)}
        </blockquote>
      </figure>
    );
  }
  if (block.type === "blockquote") {
    return (
      <blockquote className="my-5 border-l-2 border-amber-300 pl-4 py-1 text-emerald-900/85 italic leading-relaxed">
        {renderInline(block.content)}
      </blockquote>
    );
  }
  if (block.type === "hr") {
    return (
      <div className="my-8 flex items-center justify-center" aria-hidden>
        <div className="w-12 h-px bg-emerald-200" />
        <div className="mx-2 w-1.5 h-1.5 rounded-full bg-emerald-300" />
        <div className="w-12 h-px bg-emerald-200" />
      </div>
    );
  }
  // default: paragraph
  return (
    <p className="my-4 leading-relaxed text-[16px] sm:text-[17px] text-emerald-950/85">
      {renderInline(block.content)}
    </p>
  );
}


/* ── inline visual embed (social 1:1 / pinterest 2:3) ──────────────── */
function InlineVisual({ visual }) {
  if (!visual?.url) return null;
  const isPinterest = visual.variant === "pinterest";
  const aspect = isPinterest ? "aspect-[2/3]" : "aspect-square";
  return (
    <figure className={`my-8 rounded-3xl overflow-hidden border border-emerald-100 bg-emerald-50/30 ${isPinterest ? "max-w-md mx-auto" : "max-w-xl mx-auto"}`} data-testid={`editorial-inline-visual-${visual.variant}`}>
      <div className={`${aspect} overflow-hidden`}>
        <img
          src={visual.url}
          alt={visual.alt_text || visual.keyword || "Wellness illustration"}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]"
        />
      </div>
      {(visual.caption || visual.keyword) && (
        <figcaption className="px-4 py-2.5 text-[11px] text-emerald-900/55 italic text-center">
          {visual.caption || visual.keyword}
        </figcaption>
      )}
    </figure>
  );
}


/* ── premium internal-link card (used by the parent below body) ───── */
export function InternalLinkCard({ slug, title, hero_excerpt, anchor, image_url }) {
  return (
    <Link
      to={`/learn/${slug}`}
      className="group relative block rounded-2xl border border-emerald-100 bg-white overflow-hidden hover:shadow-lg hover:border-emerald-200 transition-all duration-500"
      data-testid={`editorial-internal-link-${slug}`}
    >
      {image_url && (
        <div className="aspect-[16/9] overflow-hidden bg-emerald-50/30">
          <img src={image_url} alt={title || ""} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        </div>
      )}
      <div className="p-4 sm:p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Learn more</p>
        <h4 className="font-display text-base sm:text-lg text-emerald-950 mt-1.5 leading-snug font-normal group-hover:text-emerald-800 transition-colors">
          {anchor || title}
        </h4>
        {hero_excerpt && <p className="text-[12.5px] text-emerald-900/65 mt-1.5 line-clamp-2 leading-relaxed">{hero_excerpt}</p>}
        <span className="absolute top-4 right-4 text-emerald-700/40 group-hover:text-emerald-700 transition-colors">
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}


/* ── reading progress (sticky top bar) ───────────────────────────── */
export function ReadingProgress() {
  return (
    <div
      className="fixed left-0 right-0 top-16 h-[2px] bg-emerald-100/40 z-40 pointer-events-none"
      data-testid="reading-progress"
    >
      <div id="reading-progress-bar" className="h-full bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-400 origin-left transition-transform duration-150" style={{ transform: "scaleX(0)" }} />
    </div>
  );
}


/* ── slugify export for the TOC ──────────────────────────────────── */
export { slugify };
